import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { TestDto } from '@/api/Clients/Types/TestDto'
import { clientTestTypes, getTestSubcategoryByName } from '@/api/Clients/Types/TestQuestionDto'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { Modal } from '@/components/CustomModal'
import MoreButton from '@/components/MoreButton/MoreButton'
import { AmlFieldAnswerSection } from '@/components/QuestionAnswerSection/AmlFieldAnswerSection'
import { errorToast, successToast, ToastContext } from '@/components/Toast/Toast'
import { Button } from '@/components/ui/button'
import { Heading4 } from '@/components/ui/typography'
import { useCallbackWithForceRefresh } from '@/utils/api/useCallbackWithForceRefresh'
import { AuthSessionContext } from '@/utils/AuthContext'
import { canEditEconomicProfileTestForClientStatus } from '@/utils/clients.helpers'
import { formatLocalDateUTC } from '@/utils/date'
import { handleOpenWindow } from '@/utils/openTestWindow'
import { hasPermission } from '@/utils/permissionsValidator'
import { TenantFeature } from '@/utils/Tenant'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { ClientDetailedDto, ClientNationalityDto } from 'api/Clients/Types/ClientDetailedDto'
import { Box } from 'components/Box'
import { Loading } from 'components/Loading'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchAMLCategoriesTypes } from 'store/clients/clients.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useFetchOne } from 'utils/api/useFetch'
import { getPrimary } from 'utils/getPrimary'

import { ClientTestAMLFields } from './ClientTestAMLFields'

interface Props {
  data: ClientDetailedDto
  forceRefresh: () => void
}

const ClientEconomicProfileTestPage: React.FC<Props> = ({ data, forceRefresh }) => {
  const clientsApiClient = useApiClient(ClientsApiClient)
  const clientsApiClientRef = useRef(clientsApiClient)
  const tickmillProducts = useAppSelector((state) => state.clients.tickmillProducts)
  const countries = useAppSelector((state) => state.identity.countries)
  const [auth] = useContext(AuthSessionContext)
  const setToast = useContext(ToastContext)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editableData, setEditableData] = useState<{
    isPoliticallyExposedPerson: boolean
    nationalities: ClientNationalityDto[]
    countryOfResidence: { id: string; name: string } | null
  }>({
    isPoliticallyExposedPerson: false,
    nationalities: [],
    countryOfResidence: null,
  })

  const { callback: clientEconomicProfileTests } = useCallbackWithForceRefresh(
    () => clientsApiClient.getEconomicProfileTest(data.id),
    [data.id]
  )

  const { callback: fetchAMLDetails, forceRefresh: forceRefreshAML } = useCallbackWithForceRefresh(
    () => clientsApiClient.getClientAMLDetails(data.id),
    [data.id]
  )

  const { data: tests, isLoading } = useFetchOne(clientEconomicProfileTests)
  const { data: amlData, isLoading: isLoadingAML } = useFetchOne(fetchAMLDetails)

  useEffect(() => {
    clientsApiClientRef.current = clientsApiClient
  }, [clientsApiClient])

  const dispatch = useAppDispatch()
  const amlCategories = useAppSelector((state) => state.clients.amlCategories)

  const amlDetailsFields = useMemo(() => {
    if (!amlData?.amlFields) return []

    return amlData.amlFields.filter((field) => {
      const isSpecial = ['non_face_to_face', 'dual_citizenship'].includes(field.reference)
      return isSpecial || field.amlFieldType?.id !== 10
    })
  }, [amlData?.amlFields])

  useEffect(() => {
    if (!amlCategories?.length) {
      dispatch(fetchAMLCategoriesTypes(clientsApiClientRef.current))
    }
  }, [amlCategories?.length, dispatch])

  useEffect(() => {
    if (isEditModalOpen && data) {
      const primaryAddress = getPrimary(data.addresses)
      setEditableData({
        isPoliticallyExposedPerson: data.isPoliticallyExposedPerson || false,
        nationalities: data.nationalities || [],
        countryOfResidence: primaryAddress?.country || null,
      })
    }
  }, [isEditModalOpen, data])

  const handleOpenFilledTest = ({ id, test, tickmillProductId }: TestDto) => {
    handleOpenWindow({
      urlKey: TenantFeature.clientTestFilledWindow,
      pathParams: {
        type: clientTestTypes.economicProfile,
        clientId: data.id ? data.id : '',
        testId: id ?? '',
      },
      additionalParams: {
        productId: tickmillProductId.toString(),
        subcategoryId: getTestSubcategoryByName(test.subcategory).toString(),
      },
    })
  }

  const handleEditTestForm = ({ id, test, tickmillProductId }: TestDto) => {
    handleOpenWindow({
      urlKey: TenantFeature.clientEditTestFormWindow,
      pathParams: {
        type: clientTestTypes.economicProfile,
        clientId: data.id ? data.id : '',
        testId: id ?? '',
      },
      additionalParams: {
        productId: tickmillProductId.toString(),
        subcategoryId: getTestSubcategoryByName(test.subcategory).toString(),
      },
    })
  }

  const permissions = auth?.permissions || []
  const canEditEconomicProfileTest =
    hasPermission(permissions, PermissionsEnum.CanEditEconomicProfileTest) &&
    canEditEconomicProfileTestForClientStatus(data.status.id)

  const handleEditAMLFields = () => {
    setIsEditModalOpen(true)
  }

  const handleSaveAMLFields = async () => {
    try {
      const { companyDetails, ...individualDetails } = data

      const updatedAddresses = individualDetails.addresses.map((address) => {
        if (address.isPrimary && editableData.countryOfResidence) {
          return {
            ...address,
            country: editableData.countryOfResidence,
          }
        }
        return address
      })

      const updatedData = {
        ...individualDetails,
        isPoliticallyExposedPerson: editableData.isPoliticallyExposedPerson,
        nationalities: editableData.nationalities,
        addresses: updatedAddresses,
      }

      await clientsApiClient.updateClientIndividualDetails(updatedData, true)
      setToast(successToast('AML details updated successfully'))
      setIsEditModalOpen(false)
      forceRefreshAML()
      forceRefresh()
    } catch (error) {
      console.error('Error saving AML details:', error)
      setToast(errorToast('Failed to update AML details'))
    }
  }

  const handlePEPChange = (isPEP: boolean) => {
    setEditableData({
      ...editableData,
      isPoliticallyExposedPerson: isPEP,
    })
  }

  const handleNationalitiesChange = (nationalities: ClientNationalityDto[]) => {
    setEditableData({
      ...editableData,
      nationalities,
    })
  }

  const handleCountryChange = (countryId: string) => {
    const country = countries.find((c) => c.id === countryId)
    setEditableData({
      ...editableData,
      countryOfResidence: country ? { id: country.id, name: country.name } : null,
    })
  }

  return (
    <Loading isLoading={isLoading || isLoadingAML}>
      <Box display='flex' flexDirection='column' style={{ margin: '1rem' }}>
        {isEditModalOpen && amlData && (
          <Modal visible={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
            <Box display='flex' flexDirection='column' style={{ padding: '1rem', minWidth: 600 }}>
              <Box
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                style={{ marginBottom: '1rem' }}
              >
                <Heading4>Edit AML Details</Heading4>
                <Box display='flex' gap={2}>
                  <Button type='button' variant='outline' onClick={() => setIsEditModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type='button'
                    disabled={!editableData.nationalities.some((n) => n.isPrimary)}
                    onClick={handleSaveAMLFields}
                  >
                    Save
                  </Button>
                </Box>
              </Box>

              <ClientTestAMLFields
                amlFields={amlData.amlFields}
                countryOfResidence={editableData.countryOfResidence}
                isPoliticallyExposedPerson={editableData.isPoliticallyExposedPerson}
                nationalities={editableData.nationalities}
                onCountryChange={handleCountryChange}
                onPEPChange={handlePEPChange}
                onNationalitiesChange={handleNationalitiesChange}
              />
            </Box>
          </Modal>
        )}

        {amlData && (
          <Box display='flex' flexDirection='column' style={{ marginBottom: '2rem' }}>
            <Box display='flex' alignItems='center' justifyContent='space-between'>
              <Heading4>AML Details</Heading4>
              <Button
                onClick={handleEditAMLFields}
                disabled={!hasPermission(permissions, PermissionsEnum.CanEditClient)}
                data-test='edit-aml-button'
              >
                Edit
              </Button>
            </Box>

            <AmlFieldAnswerSection
              label='AML Fields'
              items={amlDetailsFields}
              showScore={true}
              sectionScore={amlData.score}
              groupByType={true}
              isNewWindow={false}
            />
          </Box>
        )}

        <Box display='flex' mb={2} alignItems='center' justifyContent='space-between'>
          <Heading4>Economic Profile Test</Heading4>
        </Box>

        <Grid data={tests || []} resizable>
          <Column
            field='test.name'
            title='Name'
            className='table-columns__content'
            cell={(gridCellProps: GridCellProps) => (
              <td
                className='table-columns__link'
                onClick={() => handleOpenFilledTest(gridCellProps.dataItem)}
              >
                {gridCellProps.dataItem.test.name}
              </td>
            )}
          />
          <Column title='Version' field='test.version' className='table-columns__content' />
          <Column title='User' field='user.name' className='table-columns__content' />
          <Column
            field='tickmillProductId'
            title='Type'
            className='table-columns__content'
            cell={(gridCellProps: GridCellProps) => (
              <td>
                {
                  tickmillProducts.find(
                    (product) => product.id === gridCellProps.dataItem.tickmillProductId
                  )?.name
                }
              </td>
            )}
          />
          <Column
            field='dateTaken'
            title='Date Submitted'
            cell={(gridCellProps: GridCellProps) => (
              <td className='table-columns__date'>
                {formatLocalDateUTC(gridCellProps.dataItem.dateTaken, true)}
              </td>
            )}
          />
          <Column field='totalScore' title='Score' className='table-columns__number' />
          <Column field='outcome.name' title='Outcome' className='table-columns__content' />
          {canEditEconomicProfileTest && (
            <Column
              width={50}
              title=''
              cell={(gridCellProps: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name: 'Edit',
                        'data-test': 'edit',
                        onClick: () => handleEditTestForm(gridCellProps.dataItem),
                      },
                    ]}
                    data-test='more-button'
                  />
                </td>
              )}
            />
          )}
        </Grid>
      </Box>
    </Loading>
  )
}

export default ClientEconomicProfileTestPage
